import { toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { Cardinal } from '../../core/cardinal.js';
import { VersionRange } from '../../core/version-range.js';
import { isSameData } from '../../dto/data-model-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { LocalizedString } from '../localized-string.js';
import { PrintedCardCover } from '../pass-template/printed-card-cover.js';
import { Price } from '../price.js';
import { OpenAccessPassVariationDetails } from './open-access-pass-variation-details.js';

export enum PassVariationChannel {
  Online = 'Online',
  Admin = 'Admin',
  Protocol = 'Protocol',
}

export enum OpenAccessPassVariationStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export class OpenAccessPassVariation extends DataModel {
  static readonly TYPE_ID: string = '019a6c69350d4163b5e1f12b69871bdc';
  static readonly SCHEMA_VERSION: number = 4;

  private constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly status: OpenAccessPassVariationStatus,
    public readonly price: Price,
    public readonly printedCardCover: PrintedCardCover | null,
    public readonly channel: PassVariationChannel,
    public readonly maxUses: Cardinal,
  ) {
    super();
  }

  get $typeId(): string {
    return OpenAccessPassVariation.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OpenAccessPassVariation.SCHEMA_VERSION);
  }

  static create(opts: {
    uniqueId: string,
    names: LocalizedString,
    price: Price,
    printedCardCover: PrintedCardCover | null,
    channel: PassVariationChannel,
    maxUses: Cardinal,
  }): OpenAccessPassVariation {
    return new OpenAccessPassVariation(
        opts.uniqueId,
        opts.names,
        OpenAccessPassVariationStatus.Live,
        opts.price,
        opts.printedCardCover,
        opts.channel,
        opts.maxUses,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassVariation {
    return new OpenAccessPassVariation(
        MapUtils.getString(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.getRecord(map, 'names')),
        toEnumFromString(MapUtils.getStringOrNull(map, 'status'), OpenAccessPassVariationStatus),
        Price.decode(MapUtils.getString(map, 'price')),
        context.fromDataOrNull<PrintedCardCover>(MapUtils.getRecordOrNull(map, 'printedCardCover')),
        toEnumFromString(MapUtils.getStringOrNull(map, 'channel'), PassVariationChannel),
        Cardinal.decode(MapUtils.getNumber(map, 'maxUses')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'names': context.toData(this.names),
      'status': this.status,
      'price': this.price.encode(),
      'printedCardCover': context.toDataOrNull(this.printedCardCover),
      'channel': this.channel,
      'maxUses': this.maxUses.encode(),
    };
  }

  clone(): this {
    return new OpenAccessPassVariation(
        this.uniqueId,
        this.names.clone(),
        this.status,
        this.price.clone(),
        this.printedCardCover?.clone() ?? null,
        this.channel,
        this.maxUses.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.names, this.names) &&
        other.status === this.status &&
        isSameData(other.price, this.price) &&
        other.uniqueId === this.uniqueId &&
        isSameData(other.printedCardCover, this.printedCardCover) &&
        isSameData(other.maxUses, this.maxUses) &&
        other.channel === this.channel;
  }

  copyWith(data: Partial<OpenAccessPassVariation>): this {
    return new OpenAccessPassVariation(
        data.uniqueId ?? this.uniqueId,
        data.names ?? this.names.clone(),
        data.status ?? this.status,
        data.price ?? this.price.clone(),
        data.printedCardCover ?? (this.printedCardCover?.clone() ?? null),
        data.channel ?? this.channel,
        data.maxUses ?? this.maxUses.clone(),
    ) as this;
  }

  toDetails(): OpenAccessPassVariationDetails {
    return OpenAccessPassVariationDetails.create({
      uniqueId: this.uniqueId,
      names: this.names.clone(),
      price: this.price.clone(),
      channel: this.channel,
    });
  }
}

export class OpenAccessPassVariationBuilder extends DataModelBuilder<OpenAccessPassVariation> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassVariation {
    return OpenAccessPassVariation.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OpenAccessPassVariation.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenAccessPassVariation.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [uniqueId] field of type [String] was introduced
    if(version < 2) {
      // This is not really correct since you could have variations with the same price
      // but at the time this was added the feature wasn't enabled so there should be no variations in the database 🤞.
      const uniqueId = MapUtils.getString(map, 'price').toLowerCase().replaceAll(':', '_');
      map['uniqueId'] = uniqueId;

      version = 2;
    }

    // In this version the [printedCardCover] field of type [printedCardCover]
    // was introduced
    if(version < 3) {
      version = 3;
    }

    // In this version the [maxUses] field of type [Cardinal], [channel] field of type [PassVariationChannel]
    // and [status] field of type [PassVariationStatus] were introduced.
    if(version < 4) {
      map['channel'] ??= PassVariationChannel.Admin;
      map['maxUses'] ??= Cardinal.infinity.encode();
      map['status'] ??= OpenAccessPassVariationStatus.Live;

      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
